import {useEffect} from 'react';

// Define the hook's parameters and types RefObject<ControlRefObject>
export const useOutsideClick = (
  ref: React.RefObject<HTMLElement>,
  callback: (e: MouseEvent) => void,
  ignoreClasses: string[] = [],
  clickThrough: boolean = false,
  type: 'mousedown' | 'click' | 'pointerdown' = 'mousedown',
  ignoreQuerySelectors: string[] = [],
  capture: boolean = false
) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // ignore clicks on the dig-Menu-content since its overlay is not in ref
      const digMenuElement = [
        ...document.getElementsByClassName('dig-Menu-content'),
        ...document.getElementsByClassName('dig-Modal'),
        ...document.getElementsByClassName('dig-DatePicker'),
        ...document.getElementsByClassName('dig-Typeahead-container'),
        ...document.getElementsByClassName('dig-Toggletip-backdrop'),
        ...ignoreClasses.flatMap((className) =>
          Array.from(document.getElementsByClassName(className))
        ),
        ...ignoreQuerySelectors.flatMap((attribute) =>
          Array.from(document.querySelectorAll(attribute))
        ),
      ];

      // if the element node is not null and the click event target is the element node
      for (let i = 0; i < digMenuElement.length; i++) {
        if (digMenuElement[i] !== null && digMenuElement[i].contains(event.target as Node)) {
          return clickThrough;
        }
      }

      // Check if the click is on an interactive element
      const target = event.target as HTMLElement;
      const isInteractive = target.closest(
        'button, [role="button"], a, input, select, textarea, li'
      );
      if (isInteractive && type === 'click') {
        return;
      }

      // Check if the click is outside the ref element
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback(event); // Call the callback if a click outside is detected
      }
    };

    // Add the event listener
    document.addEventListener(type, handleClickOutside, capture);

    return () => {
      // Clean up the event listener
      document.removeEventListener(type, handleClickOutside, capture);
    };
  }, [ref, callback, ignoreClasses, ignoreQuerySelectors, clickThrough, type, capture]); // Re-run the hook if the ref or callback changes
};
